// src/pages/ResultsUser.tsx
import React, { useEffect, useState } from "react";
import Header from "components/Header";
import Footer from "components/Footer";

// ===== APIベース =====
const RAW_API_BASE =
  (import.meta as any).env?.VITE_API_BASE_URL ||
  (process as any).env?.REACT_APP_API_BASE_URL ||
  "";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api\/?$/, "");
const API_PUBLIC = API_BASE ? `${API_BASE}/api/public` : "/api/public";

const PAGE_SIZE = 10;

// ===== サーバーの生データ =====
type RawResult = {
  id: number | string;
  date?: string;
  raceName?: string;
  title?: string;
  item?: string;
  body?: string;
  amount?: number | string;
  badge?: string;
  publish?: boolean;
  imageUrl?: string;
  planName?: string;
  sort?: number | string; // ★ 追加：並び順
};

// ===== 画面用データ =====
type Row = {
  id: number | string;
  date: string;
  race: string;
  amount: number;
  bet: string;
  badge: "GOLD" | "SILVER" | "BRONZE" | "RAINBOW" | string;
  isPublished: boolean;
  imageUrl?: string;
  planName?: string;
  sort?: number; // ★ 追加
};

const badgeFallback: Record<string, string> = {
  GOLD: "/assets/member/jisseki_gold_big.webp",
  SILVER: "/assets/member/jisseki_silver_big.webp",
  BRONZE: "/assets/member/jisseki_bronze_big.webp",
  RAINBOW: "/assets/member/jisseki_rainbow_big.webp",
};

export default function ResultsUser() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_PUBLIC}/results`);
        const b = await r.json();

        const rawList: RawResult[] = Array.isArray(b?.items)
          ? b.items
          : Array.isArray(b)
          ? b
          : [];

        const normalized: Row[] = rawList
          .map((it) => {
            const amountNum =
              typeof it.amount === "string"
                ? Number(it.amount) || 0
                : Number(it.amount ?? 0) || 0;

            const badge =
              it.badge || (amountNum >= 1_000_000 ? "GOLD" : "SILVER");

            const sortNum =
              typeof it.sort === "string"
                ? Number(it.sort) || 0
                : Number(it.sort ?? 0) || 0;

            return {
              id: it.id,
              date: it.date || "",
              race: it.raceName || it.title || "",
              amount: amountNum,
              bet: it.item || it.body || "",
              badge,
              isPublished: it.publish !== false,
              imageUrl: it.imageUrl || "",
              planName: it.planName || "",
              sort: sortNum,
            };
          })
          .filter((x) => x.isPublished)
          .sort((a, b) => {
            // ★ 管理画面と同じ：sort 降順 → 日付降順 → id 降順
            const sa = a.sort ?? 0;
            const sb = b.sort ?? 0;
            if (sb !== sa) return sb - sa;

            const ta = a.date ? new Date(a.date).getTime() : 0;
            const tb = b.date ? new Date(b.date).getTime() : 0;
            if (tb !== ta) return tb - ta;

            return String(b.id).localeCompare(String(a.id));
          });

        setRows(normalized);
        setPage(1);
      } catch (e) {
        console.error(e);
        setRows([]);
        setPage(1);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ★ タブは廃止：単純に全部表示
  const visibleRows = rows;

  const totalPages = visibleRows.length
    ? Math.ceil(visibleRows.length / PAGE_SIZE)
    : 1;

  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const pageRows = visibleRows.slice(startIndex, endIndex);

  return (
    <>
      <Header />
      <main style={{ maxWidth: 720, margin: "20px auto", padding: "0 12px" }}>
        <h2 style={{ marginBottom: 12 }}>BOAT億MAN 的中実績</h2>

        {!loading && (
          <>
            <div style={{ display: "grid", gap: 16 }}>
              {pageRows.map((it) => {
                const img =
                  it.imageUrl ||
                  badgeFallback[it.badge] ||
                  badgeFallback["GOLD"];

                return (
                  <div
                    key={it.id}
                    style={{
                      position: "relative",
                      background: "#fff",
                      borderRadius: 12,
                      padding: 12,
                      border: "1px solid #e6edf7",
                    }}
                  >
                    {/* 的中アイコン（右上）少し大きめ */}
                    <img
                      src="/assets/member/的中.png"
                      alt="的中"
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 40,
                        height: 40,
                        objectFit: "contain",
                        pointerEvents: "none",
                      }}
                    />

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "96px 1fr",
                        gap: 12,
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={img}
                        alt=""
                        style={{
                          width: 96,
                          height: 96,
                          objectFit: "contain",
                        }}
                      />

                      <div>
                        {/* プラン名：大きく＆太字＆赤系 */}
                        {it.planName && (
                          <div
                            style={{
                              fontSize: 17,
                              fontWeight: 900,
                              marginBottom: 4,
                              color: "#d63b2b",
                            }}
                          >
                            {it.planName}
                          </div>
                        )}

                        {/* レース名 */}
                        <div style={{ fontWeight: 800 }}>{it.race}</div>

                        {/* 日付（レース名の下） */}
                        <div
                          style={{
                            fontSize: 13,
                            marginTop: 2,
                            color: "#000",
                          }}
                        >
                          {it.date}
                        </div>

                        {/* 3連単などの買目 */}
                        {it.bet && (
                          <div
                            style={{
                              fontSize: 13,
                              marginTop: 4,
                              fontWeight: 700,
                            }}
                          >
                            {it.bet}
                          </div>
                        )}

                        {/* 金額：ゴールドプレート＋白文字 */}
                        <div style={{ marginTop: 8 }}>
                          <span
                            style={{
                              background:
                                "linear-gradient(180deg,#f8d775,#f0b93a)",
                              color: "#fff",
                              padding: "6px 18px",
                              borderRadius: 999,
                              fontWeight: 900,
                              fontSize: 16,
                              display: "inline-block",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                            }}
                          >
                            {it.amount.toLocaleString()} 円 的中
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {!pageRows.length && (
                <div style={{ opacity: 0.7 }}>
                  公開中の実績はまだありません。
                </div>
              )}
            </div>

            {visibleRows.length > PAGE_SIZE && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 16,
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background: safePage <= 1 ? "#f5f5f5" : "#fff",
                    cursor: safePage <= 1 ? "default" : "pointer",
                  }}
                >
                  前へ
                </button>

                <span style={{ fontSize: 13 }}>
                  {safePage} / {totalPages}
                </span>

                <button
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={safePage >= totalPages}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background:
                      safePage >= totalPages ? "#f5f5f5" : "#fff",
                    cursor:
                      safePage >= totalPages ? "default" : "pointer",
                  }}
                >
                  次へ
                </button>
              </div>
            )}
          </>
        )}

        {loading && <div style={{ opacity: 0.7 }}>読み込み中…</div>}
      </main>
      <Footer />
    </>
  );
}
